import { useCallback, useEffect, useState } from "react";
import { Banknote, CheckCircle2, Loader2, Percent, Wallet } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Chip, Empty, Kpi, Loading, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { DateField } from "@/components/common/DateField";
import { rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { shortDate } from "@/lib/format";

export const Payouts = () => {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [form, setForm] = useState({ reference: "", paid_on: "", fee: "", note: "" });
  const [busy, setBusy] = useState(false);
  const detail = useRowDetail();

  const load = useCallback(async () => {
    const res = await api.get("/payments/payouts");
    setData(res.data);
    setPicked([]);
    setForm((f) => ({ ...f, paid_on: f.paid_on || new Date().toISOString().slice(0, 10) }));
  }, []);

  useEffect(() => { load().catch(() => setData(false)); }, [load]);

  if (!data) return <Loading label="Loading payouts" />;

  const selected = data.unsettled.filter((u) => picked.includes(u.session_id));
  const gross = Math.round(selected.reduce((s, u) => s + u.amount, 0) * 100) / 100;
  const estFee = Math.round(selected.reduce((s, u) => s + u.fee_estimate, 0) * 100) / 100;
  const fee = form.fee === "" ? estFee : Number(form.fee);
  const net = Math.round((gross - fee) * 100) / 100;

  const reconcile = async () => {
    setBusy(true);
    try {
      const res = await api.post("/payments/payouts", {
        reference: form.reference, paid_on: form.paid_on, gross, fee, net,
        session_ids: picked, note: form.note,
      });
      toast.success(`${res.data.payout_ref} reconciled — ${res.data.ledger_entries_reconciled} ledger entries ticked off`);
      setForm({ reference: "", paid_on: form.paid_on, fee: "", note: "" });
      await load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "object" ? d.message : apiError(e));
    }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="payouts-panel">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-payouts-unsettled" label="Awaiting settlement" value={data.counters.unsettled}
             accent="amber" icon={Wallet} />
        <Kpi testId="kpi-payouts-value" label="Unsettled value" accent="sky"
             value={data.counters.unsettled_value.toLocaleString()} />
        <Kpi testId="kpi-payouts-fees" label="Estimated fees" accent="violet" icon={Percent}
             value={data.counters.estimated_fees.toLocaleString()} />
        <Kpi testId="kpi-payouts-reconciled" label="Payouts reconciled" accent="emerald"
             icon={CheckCircle2} value={data.counters.reconciled} />
      </div>

      <Panel testId="panel-payout-match" kicker="Reconcile"
             title="Tie a payout to the gifts inside it"
             actions={can("payment", "approve") && (
               <Button size="sm" onClick={reconcile}
                       disabled={busy || !picked.length || !form.reference || !form.paid_on}
                       data-testid="payout-reconcile-button"
                       className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                 {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                   : <Banknote className="mr-1 h-3.5 w-3.5" />} Reconcile
               </Button>
             )}>
        <div className="grid grid-cols-1 gap-4 border-b border-border p-4 sm:grid-cols-4">
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Payout reference</Label>
            <Input value={form.reference} placeholder="po_… or bank statement line"
                   data-testid="payout-reference-input"
                   onChange={(e) => setForm({ ...form, reference: e.target.value })}
                   className="bg-muted/40 font-mono text-[12px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Paid on</Label>
            <DateField value={form.paid_on} testId="payout-date-input"
                       placeholder="Date the bank was paid"
                       onChange={(v) => setForm({ ...form, paid_on: v })}
                       className="text-[12px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Provider fee</Label>
            <Input type="number" value={form.fee} placeholder={String(estFee)}
                   data-testid="payout-fee-input"
                   onChange={(e) => setForm({ ...form, fee: e.target.value })}
                   className="bg-muted/40 font-mono text-[12px]" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-muted-foreground">Net to the bank</Label>
            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 font-mono text-[12px] text-foreground"
                 data-testid="payout-net">
              {picked.length ? net.toLocaleString() : "—"}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground sm:col-span-4">
            {picked.length} selected · gross {gross.toLocaleString()} · the fee is posted as
            "Payment processing charges" to the{" "}
            {data.fee_posting === "gift_fund" ? "fund each gift went to"
              : `central fund ${data.fee_fund_code}`}, and each matching ledger entry is marked
            reconciled.
            {data.provider_error && " The provider's payout list is unavailable with the current key, so enter the payout from your bank statement."}
          </p>
        </div>

        <Table testId="payouts-unsettled-table"
               head={<><Th /><Th>Payment</Th><Th>Donor</Th><Th>Fund</Th><Th>Amount</Th>
                 <Th>Est. fee</Th><Th>Captured</Th></>}>
          {data.unsettled.length === 0 && <Empty colSpan={7} label="Everything is settled" />}
          {data.unsettled.map((u) => (
            <tr key={u.session_id} className="row-hover" data-testid={`unsettled-row-${u.session_ref}`}>
              <Td>
                <Checkbox checked={picked.includes(u.session_id)}
                          data-testid={`unsettled-pick-${u.session_ref}`}
                          onCheckedChange={(v) => setPicked(v
                            ? [...picked, u.session_id]
                            : picked.filter((id) => id !== u.session_id))} />
              </Td>
              <Td className="font-mono text-[11px]">{u.session_ref}</Td>
              <Td>{u.donor_name}</Td>
              <Td className="font-mono text-[11px]">{u.fund_code}</Td>
              <Td className="font-mono">{u.currency} {u.amount.toLocaleString()}</Td>
              <Td className="font-mono text-[11px] text-muted-foreground">{u.fee_estimate}</Td>
              <Td className="font-mono text-[10.5px]">{shortDate(u.paid_at)}</Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Panel testId="panel-payouts-history" kicker="History" title="Reconciled payouts">
        <Table testId="payouts-table"
               head={<><Th>Reference</Th><Th>Paid on</Th><Th>Gross</Th><Th>Fee</Th><Th>Net</Th>
                 <Th>Payments</Th><Th>Ledger ticked</Th><Th>By</Th></>}>
          {data.payouts.length === 0 && <Empty colSpan={8} label="No payouts reconciled yet" />}
          {data.payouts.map((p) => (
            <tr key={p.payout_ref} className="row-hover cursor-pointer"
                data-testid={`payout-row-${p.payout_ref}`}
                onClick={rowClick(() => detail.show(p, p.payout_ref, p.payout_ref,
                                                    `${p.provider} · ${p.currency}`))}>
              <Td className="font-mono text-[11px]">{p.payout_ref}</Td>
              <Td className="font-mono text-[11px]">{shortDate(p.paid_on)}</Td>
              <Td className="font-mono">{p.gross?.toLocaleString()}</Td>
              <Td className="font-mono text-muted-foreground">{p.fee?.toLocaleString()}</Td>
              <Td className="font-mono">{p.net?.toLocaleString()}</Td>
              <Td>{p.payments?.length || 0}</Td>
              <Td>
                <Chip className="border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300">
                  {p.ledger_entries_reconciled}
                </Chip>
              </Td>
              <Td className="text-[11px] text-muted-foreground">{p.reconciled_by}</Td>
            </tr>
          ))}
        </Table>
      </Panel>
      {detail.sheet}
    </div>
  );
};
